// Firebase Error Handler Utility
export class FirebaseErrorHandler {
  static isConnectionError(error) {
    const connectionErrorCodes = [
      'unavailable',
      'deadline-exceeded',
      'resource-exhausted',
      'internal',
      'unauthenticated',
      'permission-denied'
    ]
    
    return connectionErrorCodes.includes(error?.code) || 
           error?.message?.includes('access control checks') ||
           error?.message?.includes('Failed to fetch')
  }

  static isRetryableError(error) {
    const retryableCodes = [
      'unavailable',
      'deadline-exceeded',
      'resource-exhausted',
      'internal'
    ]
    
    return retryableCodes.includes(error?.code)
  }

  static getErrorMessage(error) {
    if (this.isConnectionError(error)) {
      return 'Connection issue. Please check your internet connection and try again.'
    }
    
    switch (error?.code) {
      case 'permission-denied':
        return 'You don\'t have permission to access this resource.'
      case 'unauthenticated':
        return 'Please sign in to continue.'
      case 'not-found':
        return 'The requested resource was not found.'
      case 'already-exists':
        return 'This resource already exists.'
      case 'invalid-argument':
        return 'Invalid data provided. Please check your input.'
      case 'failed-precondition':
        return 'Operation failed due to a precondition not being met.'
      case 'aborted':
        return 'Operation was aborted.'
      case 'out-of-range':
        return 'Value is out of range.'
      case 'unimplemented':
        return 'This operation is not implemented.'
      case 'data-loss':
        return 'Data loss occurred.'
      default:
        return error?.message || 'An unexpected error occurred.'
    }
  }

  static async retryOperation(operation, maxRetries = 3, delay = 1000) {
    let lastError
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation()
      } catch (error) {
        lastError = error
        
        if (!this.isRetryableError(error) || attempt === maxRetries) {
          throw error
        }
        
        // Exponential backoff
        const waitTime = delay * Math.pow(2, attempt - 1)
        console.log(`Retry attempt ${attempt}/${maxRetries} in ${waitTime}ms...`)
        await new Promise(resolve => setTimeout(resolve, waitTime))
      }
    }
    
    throw lastError
  }

  static logError(error, context = '') {
    const isConnectionError = this.isConnectionError(error)
    const message = this.getErrorMessage(error)
    
    if (isConnectionError) {
      console.warn(`🔌 Firebase Connection Error${context ? ` (${context})` : ''}:`, message)
    } else {
      console.error(`❌ Firebase Error${context ? ` (${context})` : ''}:`, error)
    }
    
    return message
  }
}

// Global error handler for unhandled Firebase errors
export function setupFirebaseErrorHandling() {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && FirebaseErrorHandler.isConnectionError(event.reason)) {
      console.warn('🔌 Unhandled Firebase connection error:', event.reason)
      event.preventDefault() // Prevent the default error handling
    }
  })

  // Handle global errors
  window.addEventListener('error', (event) => {
    if (event.error && FirebaseErrorHandler.isConnectionError(event.error)) {
      console.warn('🔌 Global Firebase connection error:', event.error)
      event.preventDefault() // Prevent the default error handling
    }
  })
}

export default FirebaseErrorHandler 